import { ReferentialKey } from "api/referential";
import { RealEstateDto } from "api/risk";
import { TenantDto } from "api/tenant";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { Button } from "components/ui/Button";
import { GenericSurveyCompact } from "features/subscription-process/profiling-process/components/Survey/GenericSurveyPreviewCompact";
import { EyeIcon } from "components/icons/Icons";
import { getCivilityIcon } from "shared/utils/civility";
import { NsKey } from "enums/NsKey";
import { Sizes } from "enums/sizes";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { getMultipleReferentialsByKeys } from "features/referentials/slices/getMultipleReferentialsByKeysThunk";
import { useDialog } from "hooks/useDialog";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { ChevronDown, Home, MapPin, Receipt, Users } from "lucide-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectAnswers } from "../profiling-process/slices/genericSelectors";
import { formatAddress, formatEuroPrice, formatPropertyType } from "./utils/realEstateFormatters";

interface RealEstateDetailsProps {
  realEstate: RealEstateDto
  tenants?: Array<TenantDto>
  /** Shows the button opening the survey answers of the global store (default true). */
  showSurvey?: boolean
}

export const RealEstateDetails: FC<RealEstateDetailsProps> = ({ realEstate, tenants, showSurvey = true }) => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.ActorForm, NsKey.Common]);
  const [expandedTenants, setExpandedTenants] = useState<Record<string, boolean>>({});
  const answers = useAppSelector(selectAnswers);

  const civilityReferential = useSelector(selectReferentialDataByKey(ReferentialKey.Civility))
  const employmentReferential = useSelector(selectReferentialDataByKey(ReferentialKey.EmploymentStatus))
  const incomeTypeReferential = useSelector(selectReferentialDataByKey(ReferentialKey.IncomeType))

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([
      ReferentialKey.IncomeType,
      ReferentialKey.EmploymentStatus,
    ]))
  }, [dispatch])


  const getGenderIcon = useCallback((civilityId: string) => {
    const ICON_CLASS = "w-5 text-primary"
    const civilityRef = civilityReferential.find(ref => ref.id === civilityId)
    const Icon = getCivilityIcon(civilityRef?.code)
    return <Icon className={ICON_CLASS} />
  }, [civilityReferential])

  const getIncomeTypeLabel = useCallback((id: string) => {
    const ref = incomeTypeReferential.find(ref => ref.id === id)
    return ref && ref.label
  }, [incomeTypeReferential])


  const getProfessionnalSituation = useCallback((id: string) => {
    const ref = employmentReferential.find(ref => ref.id === id)
    return ref && ref.label
  }, [employmentReferential])

  // Fonction pour basculer l'état d'un locataire spécifique
  const toggleTenant = (tenantId: string) => {
    setExpandedTenants(prev => ({
      ...prev,
      [tenantId]: !prev[tenantId]
    }));
  };

  const { openBlankDialog } = useDialog()

  const openSurvey = useCallback(() => {
    openBlankDialog({
      size: Sizes["3xl"]
    }, <GenericSurveyCompact survey={answers as any} title={t('COMMON.LABELS.SURVEY_RESPONSES')} />)
  }, [answers, openBlankDialog])

  return (
    <div className="relative max-w-4xl mx-auto bg-white rounded-xl border overflow-hidden">

      {showSurvey && (
        <div className="absolute right-2 top-2 z-50">
          <Button round color="slate" className="text-sm" onClick={() => openSurvey()}>
            <EyeIcon/>
            Afficher le questionnaire
          </Button>
        </div>
      )}
      {/* Image de couverture */}
      <div className="relative h-64 bg-primary/80">
        {realEstate.photoUrl ? (
          <img
            src={realEstate.photoUrl}
            alt={realEstate.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-white">
            <Home size={64} className="opacity-50" />
          </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="absolute bottom-4 left-4 text-white">
          <h1 className="text-2xl font-bold">{realEstate.name}</h1>
          <p className="text-sm opacity-90 flex items-center mt-1">
            <MapPin size={16} className="mr-1" />
            {formatAddress(realEstate.address)}
          </p>
        </div>
      </div>

      <div className="p-6">
        {/* Informations principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{realEstate.area} m²</div>
            <div className="text-sm text-gray-600">{t('REAL_ESTATE.LABELS.AREA')}</div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{realEstate.roomCount}</div>
            <div className="text-sm text-gray-600">{t('REAL_ESTATE.LABELS.PIECES')}</div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-800">{formatPropertyType(realEstate.propertyType, t)}</div>
            <div className="text-sm text-gray-600">{t('REAL_ESTATE.LABELS.TYPE')}</div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-center">
            {
              realEstate.rentAmount &&
              <div className="text-2xl font-bold text-primary">{formatEuroPrice(realEstate.rentAmount)}</div>
            }
            <div className="text-sm text-gray-600">{t('REAL_ESTATE.LABELS.MONTHLY_RENT')}</div>
          </div>
        </div>

        {/* Charges */}
        {realEstate.charges && realEstate.charges > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <Receipt className="text-yellow-600 mr-2" size={20} />
              <span className="font-medium text-yellow-800">
                {t('REAL_ESTATE.LABELS.MONTHLY_CHARGES')} : {formatEuroPrice(realEstate.charges)}
              </span>
            </div>
          </div>
        )}

        {/* Propriétaires */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <Users className="mr-2" size={20} />
            {t('REAL_ESTATE.LABELS.OWNERS')}
          </h3>

          {realEstate.owners && realEstate.owners.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {realEstate.owners.map((owner, index) => (
                <div key={owner.id || index} className="bg-gray-50 rounded-lg p-3">
                  <div className="font-medium">{owner.displayName}</div>
                  <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                    {owner.phone && <span>📞 {owner.phone}</span>}
                    {owner.email && <span>📧 {owner.email}</span>}
                    <span className="text-xs bg-gray-200 rounded px-2 py-0.5">
                      {owner.type === 'legal' ? 'Personne morale' : 'Personne physique'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Locataires */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <Users className="mr-2" size={20} />
            {t('REAL_ESTATE.LABELS.TENANTS')}
          </h3>
          {tenants && tenants.length > 0 && tenants.map((tenant, index) => {
            const tenantKey = tenant.id || `tenant-${index}`;
            const isExpanded = expandedTenants[tenantKey] || false;

            return (
              <div
                key={tenantKey}
                className="border rounded-2xl bg-white shadow mb-3 overflow-hidden transition-all"
              >
                {/* En-tête cliquable */}
                <div
                  onClick={() => toggleTenant(tenantKey)}
                  className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Identité */}
                    <div className="col-span-4 flex items-center gap-3">
                      {tenant.civilityId && getGenderIcon(tenant.civilityId)}
                      <div>
                        <p className="font-semibold text-gray-800">
                          {tenant.lastName} {tenant.firstName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {tenant.email}
                        </p>
                      </div>
                    </div>

                    {/* Situation professionnelle */}
                    <div className="col-span-3 flex items-center gap-2">
                      {tenant.employmentStatusId && getProfessionnalSituation(tenant.employmentStatusId)}
                    </div>

                    {/* Revenus totaux */}
                    <div className="col-span-4 flex items-center gap-2">
                      <span className="text-gray-600 text-sm">Revenus :</span>
                      <span className="font-semibold text-primary">
                        {tenant.incomes?.reduce((sum, income) => sum + (income.value || 0), 0).toLocaleString('fr-FR')} €
                      </span>
                    </div>

                    {/* Icône déroulante */}
                    <div className="col-span-1 flex justify-end">
                      <ChevronDown
                        size={20}
                        className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Détails déroulants */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-2 border-t bg-gray-50 space-y-4">
                    {/* Informations de contact */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Date de naissance</p>
                        <p className="text-sm font-medium">
                          {new Date(tenant.birthDate).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Téléphone</p>
                        <p className="text-sm font-medium">{tenant.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Date d'entrée</p>
                        <p className="text-sm font-medium">
                          {new Date(tenant.checkInDate).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      {tenant.checkOutDate && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Date de sortie</p>
                          <p className="text-sm font-medium">
                            {new Date(tenant.checkOutDate).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Détails des revenus */}
                    {tenant.incomes && tenant.incomes.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Détail des revenus</p>
                        <div className="space-y-2">
                          {tenant.incomes.map((income, idx) => (
                            <div
                              key={`${income.id}-${idx}`}
                              className="flex justify-between items-center bg-white px-3 py-2 rounded-lg"
                            >
                              <span className="text-sm text-gray-700">
                                {getIncomeTypeLabel(income.incomeTypeId || 'Type de revenu')}
                              </span>
                              <span className="text-sm font-semibold text-primary">
                                {income.value?.toLocaleString('fr-FR')} €
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Garant */}
                    {tenant.guarantor && (
                      <div className="border-t pt-3">
                        <p className="text-xs text-gray-500 mb-2">Garant</p>
                        <div className="bg-white px-3 py-2 rounded-lg">
                          <p className="text-sm font-semibold text-gray-800 mb-1">
                            {tenant.guarantor.lastName} {tenant.guarantor.firstName}
                          </p>
                          <p className="text-xs text-gray-500">{tenant.guarantor.email}</p>
                          {tenant.guarantor.incomes && tenant.guarantor.incomes.length > 0 && (
                            <p className="text-xs text-gray-600 mt-1">
                              Revenus : {tenant.guarantor.incomes.reduce((sum, income) => sum + (income.value || 0), 0).toLocaleString('fr-FR')} €
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
